/**
 * Channel Connection Types
 *
 * All types in this file are MIRRORED from backend.
 * Changes to backend API contracts should be reflected here.
 *
 * Source: libs/api-contracts/src/dtos/ChannelConnectionDto.ts and libs/domain/src/enums/
 */

/**
 * Chat channel enum
 *
 * MIRRORED from backend: libs/domain/src/enums/ChatChannel.ts
 */
export enum ChatChannel {
  WHATSAPP = 'WHATSAPP',
  INSTAGRAM = 'INSTAGRAM',
  WEB = 'WEB',
}

/**
 * Chat provider enum
 *
 * MIRRORED from backend: libs/domain/src/enums/ChatProvider.ts
 */
export enum ChatProvider {
  WHATICKET = 'WHATICKET',
  WAHA = 'WAHA',
  NOTIFICAMEHUB = 'NOTIFICAMEHUB',
  CHATWOOT = 'CHATWOOT',
  FACEBOOK = 'FACEBOOK',
}

/**
 * Chat implementation enum
 *
 * MIRRORED from backend: libs/domain/src/enums/ChatImplementation.ts
 */
export enum ChatImplementation {
  BAILEYS = 'BAILEYS',
  ZAPI = 'ZAPI',
  WHATSAPP_CLOUD = 'WHATSAPP_CLOUD',
  WHATSMEOW = 'WHATSMEOW',
  GOWS = 'GOWS',
}

/**
 * DTO for creating a Whaticket WhatsApp Official channel connection
 *
 * MIRRORED from backend: libs/api-contracts/src/dtos/ChannelConnectionDto.ts
 */
export interface CreateWhaticketOfficialChannelDto {
  name: string;
  description?: string;
  apiUrl: string;
  apiKey: string;
}

/**
 * DTO for updating a Whaticket WhatsApp Official channel connection
 *
 * Special behavior for apiKey:
 * - If apiKey is "********", the existing credential is preserved (not changed)
 * - If apiKey is a different value, it will be updated (and encrypted)
 * - If apiKey is undefined/not provided, the existing credential is preserved
 *
 * MIRRORED from backend: libs/api-contracts/src/dtos/ChannelConnectionDto.ts
 */
export interface UpdateWhaticketOfficialChannelDto {
  name?: string;
  description?: string;
  apiUrl?: string;
  apiKey?: string; // Use "********" to keep existing value
}

/**
 * DTO for partial update (PATCH)
 *
 * MIRRORED from backend: libs/api-contracts/src/dtos/ChannelConnectionDto.ts
 */
export type PatchWhaticketOfficialChannelDto = Partial<UpdateWhaticketOfficialChannelDto>;

/**
 * DTO for creating a NotificameHub WhatsApp Official channel connection
 *
 * MIRRORED from backend: apps/backend/src/api/modules/channels/dtos/CreateNotificamehubOfficialChannelDto.ts
 */
export interface CreateNotificamehubOfficialChannelDto {
  name: string;
  description?: string;
  apiToken: string;
  channelId: string;
}

/**
 * DTO for updating a NotificameHub WhatsApp Official channel connection
 *
 * Special behavior for apiToken:
 * - If apiToken is "********", the existing credential is preserved (not changed)
 * - If apiToken is a different value, it will be updated (and encrypted)
 * - If apiToken is undefined/not provided, the existing credential is preserved
 *
 * MIRRORED from backend: apps/backend/src/api/modules/channels/dtos/UpdateNotificamehubOfficialChannelDto.ts
 */
export interface UpdateNotificamehubOfficialChannelDto {
  name?: string;
  description?: string;
  apiToken?: string; // Use "********" to keep existing value
  channelId?: string;
}

/**
 * DTO for partial update (PATCH) - NotificameHub
 *
 * MIRRORED from backend: apps/backend/src/api/modules/channels/dtos/PatchNotificamehubOfficialChannelDto.ts
 */
export type PatchNotificamehubOfficialChannelDto = Partial<UpdateNotificamehubOfficialChannelDto>;

/**
 * DTO for channel connection response (without credentials)
 *
 * MIRRORED from backend: libs/api-contracts/src/dtos/ChannelConnectionDto.ts
 */
export interface ChannelConnectionResponseDto {
  id: string;
  accountId: string;
  channel: string;
  provider: string;
  implementation: string;
  name: string;
  description?: string;
  status?: string;
  config?: Record<string, unknown>;
  // credentials are NEVER returned - only masked placeholders
  credentialsMask?: Record<string, string>; // e.g., { apiKey: "********" }
  createdAt: Date;
  updatedAt: Date;
  lastUsedAt?: Date;
}

/**
 * DTO for test connection request
 *
 * MIRRORED from backend: apps/backend/src/api/modules/channels/dtos/TestConnectionRequestDto.ts
 */
export interface TestConnectionRequestDto {
  testPhoneNumber?: string;
}

/**
 * DTO for test connection response
 *
 * MIRRORED from backend: libs/api-contracts/src/dtos/ChannelConnectionDto.ts
 */
export interface TestConnectionResponseDto {
  success: boolean;
  message: string;
  details?: unknown;
}

/**
 * DTO for listing channel connections
 *
 * MIRRORED from backend: libs/api-contracts/src/dtos/ChannelConnectionDto.ts
 */
export interface ListChannelConnectionsResponseDto {
  connections: ChannelConnectionResponseDto[];
  total: number;
}
